/**
 * dir-w | backOffice
 * User authentication service
 * - signUp
 * - signIn
 * - signedIn
 * - autoSignedIn
 * - signOut
 * - signedOut
 * - autoSignedOut
 * - changePassword
 * - update
 * - menus
 *
 * Last update : July 30, 2024 | @rizkiprasetya
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { Configuration } from '../config/configuration';
import { IHttpMessage, IHttpResponse } from '../interfaces/http.interface';
import { LocalStorageService } from './local-storage.service';
import { TokenService } from './token.service';
import { BaseCrudRepository } from '../repositories/base-crud.repository';
import { Auth } from '../models/auth.model';
import { UserWithReferral } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthService extends BaseCrudRepository implements OnDestroy {
  public authSubject = new BehaviorSubject<Auth | null>(null);
  private tokenExpirationTimer: any;
  private moduleEndPoint: string = `${Configuration.apiURL}/auth`;

  constructor(http: HttpClient, private _router: Router) {
    super(http);
  }

  /**
   * Free the memory from user subscription when user auth service is no longer needed.
   */
  ngOnDestroy(): void {
    if (this.authSubject) this.authSubject.unsubscribe();
  }

  /**
   * User sign in authentication
   * @param __email string
   * @param __password string
   * @returns `Observable<ISignIn>`
   */
  public signIn(
    __email: string,
    __password: string
  ): Observable<IHttpResponse> {
    return this.post(`${this.moduleEndPoint}/signIn`, {
      email: __email,
      password: __password,
    });
  }

  /**
   * After user succesfully sign in, put the user authentication
   * info into the user subscription and setup the expired time
   * for user token for 2 hours (7200 seconds) and put the user
   * authentication info on local storage for later use.
   */
  public signedIn(__res: IHttpResponse): void {
    // expired in 2 hours
    const expiredDate = new Date(
      new Date().getTime() + Configuration.tokenExpiredTime * 1000
    );

    // put user info into User model
    const resUser: UserWithReferral = new UserWithReferral(__res.authSignIn!);
    // console.log(resUser);

    // put user, token and auth in Auth Model
    const authUser: Auth = new Auth(resUser, __res.authToken, expiredDate);

    // subscribe aunthenticated user
    this.authSubject.next(authUser);

    // set auto sign out
    this.autoSignedOut(Configuration.tokenExpiredTime * 1000);

    // save logged user info into local storage
    LocalStorageService.save(Configuration.Profile, authUser);
  }

  /**
   * If user directly go into HOME ('/'), skipping the sign in page
   * check the user authentication info on local storage. Put the information
   * on user subscription and setup the auto sign out for what the times has left
   */
  public autoSignedIn(): void {
    // get user info from local storage
    const res = LocalStorageService.get(Configuration.Profile);
    if (JSON.stringify(res) === '{}') {
      this.authSubject.next(null);
      return;
    }

    // asign variable from local storage to Auth model
    const authUser = new Auth(
      res.user,
      res._token,
      res._tokenExpiredDate == null ? null : new Date(res._tokenExpiredDate)
    );

    // if authUser exists, setup the expired time base on what timess left
    if (authUser) {
      this.authSubject.next(authUser);
      const expiredIn: number =
        new Date(TokenService.expiredDate()).getTime() - new Date().getTime();
      this.autoSignedOut(expiredIn);
    }
  }

  /**
   * New user registration, supply the full name, email and password
   *
   * @param __name
   * @param __email
   * @param __password
   * @param __passwordConfirmation
   * @returns `Observable<ISignUp>`
   */
  public signUp(
    __name: string,
    __email: string,
    __mobile: string,
    __password: string,
    __passwordConfirmation: string
  ): Observable<IHttpResponse> {
    return this.post(`${this.moduleEndPoint}/signUp`, {
      name: __name,
      email: __email,
      mobile: __mobile,
      password: __password,
      password_confirmation: __passwordConfirmation,
    });
  }

  /**
   * User sign out the system, clear curren token from tables access tokens
   * @returns `Observable<IMessage>`
   */
  public signOut(): Observable<IHttpMessage> {
    return this.patch(`${this.moduleEndPoint}/signOut`, []);
  }

  /**
   * After sign out complete, clear the user subscription,
   * delete the user authentication info from local storage
   * and finally redirect user to sign in page
   */
  public signedOut(): void {
    this.authSubject.next(null);
    LocalStorageService.remove(Configuration.Profile);
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }
    this.tokenExpirationTimer = null;
  }

  /**
   * Prepare the setTimeout with the what's left on session expired time
   * on user authentication info on local storage, and when the time is came
   * redirect user to sign out page with session as the reason
   * @param __expiredDuration
   */
  public autoSignedOut(__expiredDuration: number): void {
    this.tokenExpirationTimer = setTimeout(() => {
      this._router.navigate(['/auth/signout'], {
        queryParams: { reason: 'session' },
      });
    }, __expiredDuration);
  }

  /**
   * Get the logged user profile information
   *
   * @returns `Observable<IProfile>`
   */
  public profile(): Observable<IHttpResponse> {
    return this.get(`${this.moduleEndPoint}/profile`, []);
  }

  /**
   * Change the user password, validate the current password first
   * Make sure the new password pass the password validation
   * @param __currentPassword
   * @param __newPassword
   * @param __newPasswordConfirmation
   * @returns `Observable<IMessage>`
   */
  public changePassword(
    __currentPassword: string,
    __newPassword: string,
    __newPasswordConfirmation: string
  ): Observable<IHttpMessage> {
    return this.patch(`${this.moduleEndPoint}/changePassword`, {
      current_password: __currentPassword,
      new_password: __newPassword,
      new_password_confirmation: __newPasswordConfirmation,
    });
  }

  /**
   * Update user information such as name and email
   * If email changes, user must be verified the new email again
   * through email verification process like the new user sign up
   * @param __email
   * @param __mobile
   * @param __first_name
   * @param __last_name
   * @param __nick_name
   * @param __birth_place
   * @param __birth_date
   * @param __gender
   * @param __religion
   * @param __marital
   * @param __nationality
   * @param __blood_type
   * @param __nik
   * @param __weight
   * @param __height
   * @param __emergency_contact
   * @returns `Observavble<IAuthUpdate>`
   */
  public update(
    __email: string,
    __mobile: string | null,
    __first_name: string,
    __last_name: string | null,
    __nick_name: string | null,
    __birth_place: string | null,
    __birth_date: Date | null,
    __gender: string | null,
    __religion: string | null,
    __marital: string | null,
    __nationality: string | null,
    __blood_type: string | null,
    __nik: string | null,
    __weight: number | null,
    __height: number | null,
    __emergency_contact: string | null
  ): Observable<IHttpResponse> {
    return this.patch(`${this.moduleEndPoint}/update`, {
      email: __email,
      mobile: __mobile,
      first_name: __first_name,
      last_name: __last_name,
      nick_name: __nick_name,
      birth_place: __birth_place,
      birth_date: __birth_date,
      gender: __gender,
      religion: __religion,
      marital: __marital,
      nationality: __nationality,
      blood_type: __blood_type,
      nik: __nik,
      weight: __weight,
      height: __height,
      emergency_contact: __emergency_contact,
    });
  }

  /**
   * Update user photo profile
   * @param __file_photo
   */
  public updatePhoto(__formData: FormData | null): Observable<IHttpResponse> {
    return this.post(`${this.moduleEndPoint}/updatePhoto`, __formData, false);
  }

  /**
   * Get access menus for authenticated used
   * @returns `Observable<IHttpResponse>`
   */
  public menus(): Observable<IHttpResponse> {
    return this.get(`${this.moduleEndPoint}/menus`, []);
  }
}
